import { Divider } from 'src/design/atoms/Divider';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { DisclosurePopover } from 'src/design/molecules/DisclosurePopover';
import { Table } from 'src/design/molecules/Table';
import { IconThumbDown, IconThumbUp, IconTilde } from '@tabler/icons-react';
import moment from 'moment';
import React, { useMemo } from 'react';
import { DailyDto } from 'src/api/FiretrolApiClient';
import { formatWorkWeekId, getCurrentWorkWeek } from 'src/utils/workWeek';
import { useDailys } from 'src/api/queries/dailys/useDailys';
import { useDailySubmissionReport } from 'src/api/queries/dailys/useDailySubmissionReport';
import { useAuth } from 'src/hooks/useAuth';
import { formatDate, toUtcDateKey } from 'src/utils';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { Header } from 'src/design/organisms/Header';
import {
  currentWeekFilter,
  tempOptions,
  WeekSelector,
} from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';

interface DailyCountData {
  count: number;
  countTimesheets: number;
  countProgress: number;
}

interface UserDailySummary {
  userId: number;
  fullName: string;
  dailyCounts: { [date: string]: DailyCountData };
}

interface DailyTooltipContentProps {
  userId: number;
  date: string;
}

const EMPTY_DAILYS: DailyDto[] = [];

const DailyTooltipContent: React.FC<DailyTooltipContentProps> = ({
  userId,
  date,
}) => {
  const dateOnly = React.useMemo(() => new Date(date), [date]);
  const {
    data: dailies = EMPTY_DAILYS,
    isLoading,
    isError,
  } = useDailys(
    { userId, startDate: dateOnly, endDate: dateOnly },
    { enabled: !!userId && !!date },
  );

  if (isLoading) {
    return <Text size="sm">Loading...</Text>;
  }

  if (isError) {
    return (
      <Text size="sm" c="red">
        Failed to load dailies
      </Text>
    );
  }

  if (dailies.length === 0) {
    return <Text size="sm">No dailies found</Text>;
  }

  return (
    <Stack gap="xs" maw={400}>
      {dailies.map((daily) => {
        const exclusionMessages: string[] = [];
        if (daily.excludeImages && daily.exclusionImagesReason) {
          exclusionMessages.push(`Images: ${daily.exclusionImagesReason}`);
        }
        if (daily.excludeProgress && daily.exclusionProgressReason) {
          exclusionMessages.push(`Progress: ${daily.exclusionProgressReason}`);
        }
        if (daily.excludeQuestions && daily.exclusionQuestionsReason) {
          exclusionMessages.push(
            `Questions: ${daily.exclusionQuestionsReason}`,
          );
        }
        if (daily.excludeTickets && daily.exclusionTicketsReason) {
          exclusionMessages.push(`Tickets: ${daily.exclusionTicketsReason}`);
        }
        if (daily.excludeTimesheets && daily.exclusionTimesheetsReason) {
          exclusionMessages.push(
            `Timesheets: ${daily.exclusionTimesheetsReason}`,
          );
        }

        return (
          <div key={daily.id}>
            <LinkButton
              size="sm"
              mb="xs"
              to={`/jobs/${daily.jobId}/dailys/view/${daily.id}`}
              target="_blank"
            >
              {daily.dailyNumber}
            </LinkButton>
            {exclusionMessages.length > 0 ? (
              <Stack gap="xs">
                {exclusionMessages.map((msg, idx) => (
                  <Text key={idx} size="xs">
                    {msg}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size="xs">No exclusion messages</Text>
            )}
          </div>
        );
      })}
    </Stack>
  );
};

export const DailySubmissionReport: React.FC = () => {
  const { user } = useAuth();
  const [selectedWeek, setSelectedWeek] = React.useState<IDateOption>(
    tempOptions.find(
      (o) =>
        o.id ===
        formatWorkWeekId(
          getCurrentWorkWeek().year,
          getCurrentWorkWeek().weekNumber,
        ),
    ) || currentWeekFilter,
  );
  const [showOnlyMyTeam, setShowOnlyMyTeam] = React.useState(false);
  // The report renders nothing until the user asks for it once; from then on
  // it re-runs whenever the week or team filter changes.
  const [generated, setGenerated] = React.useState(false);

  const { data, isFetching: loading } = useDailySubmissionReport(
    {
      startDate: selectedWeek.startDate,
      endDate: selectedWeek.endDate,
      onlyMyTeam: showOnlyMyTeam || undefined,
    },
    { enabled: generated },
  );

  const isEmpty = generated && !loading && (data?.length ?? 0) === 0;

  const generateReport = () => setGenerated(true);

  const tableData = useMemo(() => {
    if (!data) return [];

    const userMap = new Map<number, UserDailySummary>();

    data.forEach((item) => {
      if (!item.userId || !item.fullName || !item.date) return;

      if (!userMap.has(item.userId)) {
        userMap.set(item.userId, {
          userId: item.userId,
          fullName: item.fullName,
          dailyCounts: {},
        });
      }

      const dateKey = toUtcDateKey(item.date);
      userMap.get(item.userId)!.dailyCounts[dateKey] = {
        count: item.count,
        countTimesheets: item.countTimesheets || 0,
        countProgress: item.countProgress || 0,
      };
    });

    return Array.from(userMap.values());
  }, [data]);

  const weekDates = useMemo(() => {
    const dates: string[] = [];
    const start = moment(selectedWeek.startDate);
    const end = moment(selectedWeek.endDate);

    for (
      let date = start.clone();
      date.isSameOrBefore(end);
      date.add(1, 'day')
    ) {
      dates.push(date.format('YYYY-MM-DD'));
    }

    return dates;
  }, [selectedWeek]);

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Reports', href: '/reports' },
            { text: 'Daily Submission Report' },
          ],
        }}
        title="Daily Submission Report"
        helpText="Generate submission reports for daily reports for your organization. Hover over a cell to view the dailies for that day."
      />
      <Stack>
        <Grid>
          <Grid.Col span={{ base: 12, lg: 3 }}>
            <WeekSelector
              hideLabel
              value={selectedWeek}
              onChange={(v) => setSelectedWeek(v)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Group>
              <BasicButton
                disabled={loading}
                onClick={generateReport}
                loading={loading}
              >
                Generate Report
              </BasicButton>
              <Divider orientation="vertical" />
              <Checkbox
                label="Show Only My Team"
                description="Only show dailies for users that are supervised by you"
                checked={showOnlyMyTeam}
                onChange={(e) => setShowOnlyMyTeam(e.currentTarget.checked)}
              />
            </Group>
          </Grid.Col>
        </Grid>
        {isEmpty && <Text ta="center">No data for selected week</Text>}
        {data && !isEmpty && (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  {weekDates.map((date) => (
                    <Table.Th key={date}>
                      {formatDate(date, 'ddd M/D')}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tableData.map((user) => {
                  return (
                    <Table.Tr key={user.userId}>
                      <Table.Td>{user.fullName}</Table.Td>
                      {weekDates.map((date) => {
                        const dailyData = user.dailyCounts[date] || {
                          count: 0,
                          countTimesheets: 0,
                          countProgress: 0,
                        };
                        const { count, countTimesheets, countProgress } =
                          dailyData;

                        let bgColor: string;
                        let icon: React.ReactNode;
                        let textColor = 'black';

                        if (count === 0) {
                          bgColor = 'red.7';
                          icon = <IconThumbDown size={16} color="white" />;
                          textColor = 'white';
                        } else if (
                          countTimesheets === 0 ||
                          countProgress === 0
                        ) {
                          bgColor = 'yellow.7';
                          icon = <IconTilde size={16} color="white" />;
                          textColor = 'white';
                        } else {
                          icon = <IconThumbUp size={16} />;
                        }

                        return (
                          <Table.Td key={date} bg={bgColor} h={50}>
                            {count === 0 ? (
                              <Group gap="xs" wrap="nowrap">
                                <Text c={textColor}>{count}</Text>
                                {icon}
                              </Group>
                            ) : (
                              <DisclosurePopover
                                label={`${count} dailies from ${
                                  user.fullName
                                } on ${formatDate(date)}`}
                                width={400}
                                content={
                                  <DailyTooltipContent
                                    userId={user.userId}
                                    date={date}
                                  />
                                }
                              >
                                <UnstyledButton>
                                  <Group gap="xs" wrap="nowrap">
                                    <Text c={textColor}>{count}</Text>
                                    {icon}
                                  </Group>
                                  <Text c={textColor} size="xs">
                                    {countTimesheets}/{count} ts,{' '}
                                    {countProgress}/{count} pg
                                  </Text>
                                </UnstyledButton>
                              </DisclosurePopover>
                            )}
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </>
  );
};
